import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader, CareerPath, CareerCategory } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Button, SxProps, Theme, useMediaQuery, Dialog } from '@mui/material';

import { WhatIsACareerPathModalContent } from '../CareerPath/molecules/careerPathPage/WhatIsACareerPathModalContent';
import { WhatIsACareerTrackModalContent } from '../CareerTrack/WhatIsACareerTrackModalContent';

const STRINGS = appStrings.modules.survey.header;

type SurveyPageHeaderProps = Readonly<{
  userHasBlueprint: boolean;
  careerPaths: CareerPath[];
  careerCategories: CareerCategory[];
}>;

export const SurveyPageHeader = ({
  userHasBlueprint,
  careerPaths,
  careerCategories,
}: SurveyPageHeaderProps) => {
  type ModalType = 'careerTrack' | 'careerPaths' | null;
  const [isModalOpen, setIsModalOpen] = useState<ModalType>(null);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 600px)');

  const handleMyBlueprintClick = () => {
    if (userHasBlueprint) {
      navigate(APP_ROUTE.MY_BLUEPRINT);
    }
  };

  const actionButtons = (
    <Box sx={actionButtonsContainerStyle}>
      <Button
        variant="contained"
        size="md"
        hierarchy="Secondary gray"
        sx={{ minWidth: 'fit-content' }}
        onClick={() => setIsModalOpen('careerTrack')}
        id="survey-career-tracks-button"
      >
        {STRINGS.careerTracks}
      </Button>
      <Button
        variant="contained"
        size="md"
        hierarchy="Secondary gray"
        sx={{ minWidth: 'fit-content' }}
        onClick={() => setIsModalOpen('careerPaths')}
        id="survey-career-paths-button"
      >
        {STRINGS.careerPaths}
      </Button>

      <Button
        variant="contained"
        size="md"
        hierarchy="Secondary gray"
        sx={{ minWidth: 'fit-content' }}
        onClick={handleMyBlueprintClick}
        disabled={!userHasBlueprint}
        id="survey-my-blueprint-button"
      >
        {STRINGS.myBlueprint}
      </Button>
    </Box>
  );

  return (
    <>
      <PageHeader
        title={STRINGS.title}
        subtitle={STRINGS.subtitle}
        rightComponents={isMobile ? [] : [actionButtons]}
        maxWidth={null}
        showBorder={!isMobile}
        sxOverrides={{
          titleContainer: titleContainerSx,
          container: containerSx,
        }}
      />

      <Dialog
        open={isModalOpen !== null}
        onClose={() => setIsModalOpen(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: dialogSx,
        }}
      >
        {isModalOpen === 'careerTrack' && (
          <WhatIsACareerTrackModalContent onClose={() => setIsModalOpen(null)} />
        )}
        {isModalOpen === 'careerPaths' && (
          <WhatIsACareerPathModalContent
            onClose={() => setIsModalOpen(null)}
            careerPaths={careerPaths}
            careerCategories={careerCategories}
          />
        )}
      </Dialog>
    </>
  );
};

const actionButtonsContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
  pb: backrs.spacing['spacing-3xl'],
  '@container (max-width: 920px)': {
    pb: backrs.spacing['spacing-none'],
  },
});

const containerSx: SxProps<Theme> = {
  '@container (max-width: 920px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
};

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-xs'],
});

const dialogSx: SxProps<Theme> = ({ backrs }) => ({
  maxWidth: '90vw',
  maxHeight: '90vh',
  borderRadius: backrs.radius['radius-2xl'],
});
